import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import SearchBar from '../components/SearchBar';
import Pagination from '../components/Pagination';
import { PaginationInfo } from '../types';
import '../styles/RecepcionMercancia.css';
import '../styles/ProductPage.css';

interface Factura {
    id: number;
    numero_factura: string;
    fecha_emision: string;
    estado: string;
    total_total: number;
    Proveedor?: {
        nombre: string;
    };
    HistorialCompras?: any[];
    HistorialCompra?: any[];
}

interface FacturasResponse {
    facturas: Factura[];
    pagination: PaginationInfo;
}

const HistorialFacturas: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [facturas, setFacturas] = useState<Factura[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [facturaSeleccionada, setFacturaSeleccionada] = useState<Factura | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pagination, setPagination] = useState<Partial<PaginationInfo>>({});

    // Extract query from URL
    const queryParams = new URLSearchParams(location.search);
    const currentQuery = queryParams.get('q') || '';
    const isSearching = !!currentQuery.trim();

    const cargarFacturas = useCallback(async () => {
        setLoading(true);
        try {
            let res;
            if (isSearching) {
                res = await api.get<FacturasResponse>(`/facturas?query=${encodeURIComponent(currentQuery)}&page=${currentPage}&limit=10`);
            } else {
                res = await api.get<FacturasResponse>(`/facturas?page=${currentPage}&limit=10`);
            }
            setFacturas(Array.isArray(res.data?.facturas) ? res.data.facturas : []);
            setPagination(res.data?.pagination || {});
            setError('');
        } catch (err: any) {
            setError(err.response?.data?.error || 'Error al cargar facturas');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [currentQuery, currentPage, isSearching]);

    useEffect(() => {
        cargarFacturas();
    }, [cargarFacturas]);

    const handleSearch = (query: string) => {
        setCurrentPage(1);
        if (query.trim() !== '') {
            navigate(`?q=${encodeURIComponent(query)}`, { replace: true });
        } else {
            navigate(location.pathname, { replace: true });
        }
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const verDetalle = (factura: Factura) => {
        setFacturaSeleccionada(factura);
    };

    const cerrarDetalle = () => {
        setFacturaSeleccionada(null);
    };

    const getEstadoColor = (estado: string) => {
        switch (estado) {
            case 'Recibida':
            case 'Procesada':
                return '#10b981'; // Green
            case 'Borrador':
                return '#f59e0b'; // Amber
            default:
                return '#6b7280'; // Gray
        }
    };

    return (
        <div className="recepcion-container">
            <h2 className="recepcion-title">Historial de Facturas de Proveedores</h2>

            {error && <div style={{ backgroundColor: '#fee2e2', color: '#dc2626', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1rem' }}>{error}</div>}

            <SearchBar
                placeholder="Buscar facturas por número, proveedor o estado..."
                onSearch={handleSearch}
            />

            {isSearching && (
                <div className="search-results-banner" style={{ marginBottom: '1rem' }}>
                    <span className="search-results-text">
                        Resultados para: &quot;{currentQuery}&quot;
                        {pagination.totalItems !== undefined && ` (${pagination.totalItems} encontrados)`}
                    </span>
                    <button
                        className="clear-search-btn"
                        onClick={() => navigate(location.pathname)}
                    >
                        Limpiar búsqueda
                    </button>
                </div>
            )}

            {loading ? (
                <p>Cargando facturas...</p>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table className="recepcion-table">
                        <thead>
                            <tr>
                                <th>N° Factura</th>
                                <th>Fecha Emisión</th>
                                <th>Proveedor</th>
                                <th>Estado</th>
                                <th>Total</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(!Array.isArray(facturas) || facturas.length === 0) && (
                                <tr>
                                    <td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>
                                        {isSearching ? 'No se encontraron facturas' : 'No hay facturas registradas.'}
                                    </td>
                                </tr>
                            )}
                            {Array.isArray(facturas) && facturas.map((factura) => (
                                <tr key={factura.id}>
                                    <td style={{ fontWeight: 'bold' }}>{factura.numero_factura}</td>
                                    <td>{new Date(factura.fecha_emision).toLocaleDateString()}</td>
                                    <td>{factura.Proveedor?.nombre || 'Desconocido'}</td>
                                    <td>
                                        <span style={{
                                            backgroundColor: getEstadoColor(factura.estado),
                                            color: 'white',
                                            padding: '0.25rem 0.5rem',
                                            borderRadius: '9999px',
                                            fontSize: '0.875rem',
                                            fontWeight: 'bold'
                                        }}>
                                            {factura.estado}
                                        </span>
                                    </td>
                                    <td style={{ fontWeight: 'bold' }}>${(factura.total_total || 0).toLocaleString()}</td>
                                    <td>
                                        <button
                                            className="recepcion-btn-secondary"
                                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}
                                            onClick={() => verDetalle(factura)}
                                        >
                                            Ver Detalles
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Pagination
                currentPage={pagination.currentPage || 1}
                totalPages={pagination.totalPages || 1}
                hasNextPage={currentPage < (pagination.totalPages || 1)}
                hasPrevPage={currentPage > 1}
                onPageChange={handlePageChange}
            />

            {/* Modal de Detalles */}
            {facturaSeleccionada && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white', padding: '2rem', borderRadius: '0.5rem', width: '90%', maxWidth: '900px',
                        maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '1rem' }}>
                            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
                                Detalle de Factura: {facturaSeleccionada.numero_factura}
                            </h3>
                            <button onClick={cerrarDetalle} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280' }}>&times;</button>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '2rem', backgroundColor: '#f9fafb', padding: '1rem', borderRadius: '0.5rem' }}>
                            <div>
                                <p style={{ margin: '0 0 0.5rem 0', color: '#6b7280', fontSize: '0.875rem' }}>Proveedor</p>
                                <p style={{ margin: 0, fontWeight: 'bold', fontSize: '1.125rem' }}>{facturaSeleccionada.Proveedor?.nombre || 'N/A'}</p>
                            </div>
                            <div>
                                <p style={{ margin: '0 0 0.5rem 0', color: '#6b7280', fontSize: '0.875rem' }}>Fecha de Emisión</p>
                                <p style={{ margin: 0, fontWeight: 'bold', fontSize: '1.125rem' }}>{new Date(facturaSeleccionada.fecha_emision).toLocaleDateString()}</p>
                            </div>
                            <div>
                                <p style={{ margin: '0 0 0.5rem 0', color: '#6b7280', fontSize: '0.875rem' }}>Estado</p>
                                <p style={{ margin: 0, fontWeight: 'bold', fontSize: '1.125rem', color: getEstadoColor(facturaSeleccionada.estado) }}>{facturaSeleccionada.estado}</p>
                            </div>
                            <div>
                                <p style={{ margin: '0 0 0.5rem 0', color: '#6b7280', fontSize: '0.875rem' }}>Total General</p>
                                <p style={{ margin: 0, fontWeight: 'bold', fontSize: '1.125rem' }}>${(facturaSeleccionada.total_total || 0).toLocaleString()}</p>
                            </div>
                        </div>

                        <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>Ítems Recibidos</h4>

                        <div style={{ overflowX: 'auto' }}>
                            <table className="recepcion-table" style={{ fontSize: '0.875rem' }}>
                                <thead>
                                    <tr>
                                        <th>Producto</th>
                                        <th>EAN / Ref</th>
                                        <th>Q. Factura</th>
                                        <th>Q. Física (Recibida)</th>
                                        <th>Precio Unit.</th>
                                        <th>Total Ítem</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        const facturaItems = facturaSeleccionada.HistorialCompras || facturaSeleccionada.HistorialCompra || [];
                                        if (!Array.isArray(facturaItems) || facturaItems.length === 0) {
                                            return (
                                                <tr>
                                                    <td colSpan={6} style={{ textAlign: 'center', padding: '1rem' }}>No hay ítems registrados en esta factura.</td>
                                                </tr>
                                            );
                                        }
                                        return facturaItems.map((item: any, idx: number) => (
                                            <tr key={idx}>
                                                <td style={{ fontWeight: 'bold' }}>
                                                    <Link
                                                        to={`/productos/${item.producto_id || item.Producto?.id}`}
                                                        style={{ color: '#2563eb', textDecoration: 'none' }}
                                                        className="pdp-link"
                                                    >
                                                        {item.Producto?.nombre || 'Desconocido'}
                                                    </Link>
                                                </td>
                                                <td style={{ color: '#6b7280' }}>{item.Producto?.codigo_barras || item.Producto?.referencia || '-'}</td>
                                                <td>{item.cantidad_facturada || 0}</td>
                                                <td style={{ fontWeight: 'bold', color: item.cantidad_fisica !== item.cantidad_facturada ? '#d97706' : 'inherit' }}>
                                                    {item.cantidad_fisica || 0}
                                                </td>
                                                <td>${Number(item.precioUnitario).toLocaleString()}</td>
                                                <td style={{ fontWeight: 'bold' }}>${Number(item.total).toLocaleString()}</td>
                                            </tr>
                                        ));
                                    })()}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HistorialFacturas;
